import React, {
  forwardRef,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import styled, { keyframes } from "styled-components";
import toast from "react-hot-toast";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import Table, { TableColumn } from "../table/Table";
import Avatar from "../avatar/Avatar";
import DropdownMenu, { DropdownMenuItem } from "../dropdown-menu/DropdownMenu";
import Modal from "../modal/Modal";
import ConfirmModal from "../modal/ConfirmModal";
import MediaPreviewModal from "../media-preview/components/MediaPreviewModal";
import MediaTranscriptionModal from "../media-preview/components/MediaTranscriptionModal";
import BulkTranscriptionModal, {
  BulkTranscriptionTarget,
} from "../transcription/BulkTranscriptionModal";
import TranscriptionStatusCell from "../transcription/TranscriptionStatusCell";
import {
  collectTranscriptionTargets,
  isTranscribableFileType,
} from "../transcription/transcriptionTargets";
import useTranscriptionStatuses, {
  TranscriptionCellInfo,
} from "../../hooks/useTranscriptionStatuses";
import BulkDownloadProgressModal from "../message/BulkDownloadProgressModal";
import InlineLoading from "../loading/InlineLoading";
import StatusBadge from "../status-badge/StatusBadge";
import { useBulkDownload } from "../../hooks/useBulkDownload";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  DROP_DWELL_FILL,
  DROP_DWELL_LINE,
  DROP_DWELL_MS,
  DROP_READY_FILL,
  dropTargetRowStyle,
  useDropDwellGate,
  useDropDwellReady,
} from "../../hooks/useDropDwell";
import { MediaType } from "../../types/media-preview";
import { getFileIconFromCategoryOrExtension } from "../file-icon/fileIcon";
import { downloadFile } from "../../utils/downloadFile";
import {
  useAudioAudition,
  AuditionItem,
  AuditionRowProps,
} from "../audio-preview/core/useAudioAudition";
import AudioInspectorBar, {
  AUDIO_INSPECTOR_BAR_HEIGHT,
  AudioInspectorBookmark,
  AudioInspectorFocusRequest,
} from "../audio-preview/AudioInspectorBar";
import {
  containsNativeFiles,
  extractDropEntries,
} from "../../utils/dataTransferEntries";
import type { FileWithPath } from "../../hooks/useS3Upload";
import type { ZipFileEntry } from "../../utils/downloadFilesAsZip";
import FileBrowserShareModal, {
  FileBrowserShareAdapter,
  FileBrowserShareTarget,
} from "./FileBrowserShareModal";
import {
  FileBrowserFile,
  FileBrowserFolderDirectStats,
  FileBrowserFolderNode,
  FileBrowserFolderInfo,
  FileBrowserSortField,
  FileBrowserSortState,
  buildFileBrowserTree,
  collectFolderNodeIds,
  flattenFolderNodeToFiles,
  flattenFolderNodeToZipEntries,
  formatDateTime,
  formatFileSize,
  sortFileBrowserFiles,
  sortFileBrowserTree,
} from "./fileBrowserUtils";

// ===== Props =====

export interface FileBrowserProps {
  /** 表示する全ファイル（フラットリスト。folderId で階層を復元する） */
  files: FileBrowserFile[];
  /** 表示する全フォルダ（空フォルダ含むフラットリスト） */
  folders: FileBrowserFolderInfo[];
  /**
   * 検索テキスト（フォルダ名・ファイル名の部分一致）。
   * debounce・低優先レンダー化は内部で行うため、呼び出し側での遅延処理は不要。
   */
  searchText?: string;
  /** データ読み込み中はテーブル最下部にローディング行を表示する */
  isLoading?: boolean;
  /**
   * フォルダ ID → 直下集計（BE 提供）。渡すとフォルダ行の件数・サイズ・
   * 最終更新を「直下集計 + 子フォルダの畳み込み」で計算する
   * （files が遅延ロードで一部しか無い呼び出し側用）。
   */
  folderStats?: ReadonlyMap<string, FileBrowserFolderDirectStats>;
  /**
   * 直下ファイルがロード済みのフォルダ ID 集合（遅延ロード用）。
   * 未指定 = 全フォルダロード済み扱い。展開したフォルダが未ロードの
   * 場合、直下ファイルの代わりにローディング行を表示する。
   */
  loadedFolderIds?: ReadonlySet<string>;
  /**
   * フォルダを展開したとき（遅延ロードのトリガー）。呼び出し側は
   * 未ロードならそのフォルダ直下のファイルを取得してキャッシュへ足す。
   */
  // eslint-disable-next-line no-unused-vars
  onExpandFolder?: (folderId: string) => void;
  /**
   * zip 一括ダウンロードのエントリを解決する（遅延ロード用）。
   * 未指定なら手元のツリー（渡された files）から組み立てる。
   */
  // eslint-disable-next-line no-unused-vars
  resolveZipEntries?: (node: FileBrowserFolderNode) => Promise<ZipFileEntry[]>;
  /**
   * 文字起こし機能（ファイル行のステータスチップ・単体実行メニュー・
   * フォルダの一括実行メニュー）を有効にする。
   * バックエンドのメディア横断レジストリが file.id を解決できる呼び出し側
   * （共有ライブラリ等）でのみ true にする。
   */
  enableTranscription?: boolean;
  /**
   * 一括文字起こしの対象ファイルを解決する（遅延ロード用）。
   * 未指定なら手元のツリー（渡された files）から集める。
   * zip 用の resolveZipEntries とは別で、こちらは fileType の判定に
   * FileBrowserFile がそのまま必要。
   */
  resolveFolderFiles?: (
    // eslint-disable-next-line no-unused-vars
    node: FileBrowserFolderNode,
  ) => Promise<FileBrowserFile[]>;
  /** ファイルが1件も無いときのメッセージ */
  emptyMessage?: string;
  /**
   * ルート直下への編集操作（ルートへの移動・ルートへの外部D&Dアップロード）可否。
   * フォルダ・ファイル個別の可否は各アイテムの canEdit で制御する。
   */
  canEditRoot?: boolean;
  /** ダウンロード実行の通知（既読記録など。単体・zip 一括の両方から呼ばれる） */
  // eslint-disable-next-line no-unused-vars
  onFileDownloaded?: (fileId: string) => void;
  /** リネーム（未指定ならメニュー非表示） */
  // eslint-disable-next-line no-unused-vars
  onRenameFile?: (file: FileBrowserFile, newName: string) => Promise<void>;
  /** 移動（未指定ならD&D・ルートへ移動を無効化）。folderId=null はルート */
  onMoveFile?: (
    // eslint-disable-next-line no-unused-vars
    file: FileBrowserFile,
    // eslint-disable-next-line no-unused-vars
    folderId: string | null,
  ) => Promise<void>;
  /** 差し替え（未指定ならメニュー非表示） */
  // eslint-disable-next-line no-unused-vars
  onReplaceFile?: (file: FileBrowserFile, newFile: File) => Promise<void>;
  /** 削除（未指定ならメニュー非表示） */
  // eslint-disable-next-line no-unused-vars
  onDeleteFile?: (file: FileBrowserFile) => Promise<void>;
  /**
   * 外部（OS）からの D&D アップロード（未指定なら無効）。
   * folderId=null はルート直下への追加。トーストは呼び出し側で表示する。
   * フォルダごとのドロップに対応し、relativePath（ファイル名込みの相対パス。
   * 例: "フォルダ/サブ/file.mp3"）から呼び出し側で階層を復元する。
   */
  onUploadFiles?: (
    // eslint-disable-next-line no-unused-vars
    entries: FileWithPath[],
    // eslint-disable-next-line no-unused-vars
    folderId: string | null,
  ) => Promise<void>;
  /**
   * フォルダ作成（未指定ならメニュー非表示）。parentId=null はルート直下。
   * 作成したフォルダの ID を返すと、行の出現を待ってスクロール + ハイライト
   * する（大量の行の中で「どこに増えたか」を見失わないようにするため）。
   */
  onCreateFolder?: (
    // eslint-disable-next-line no-unused-vars
    name: string,
    // eslint-disable-next-line no-unused-vars
    parentId: string | null,
  ) => Promise<string | void>;
  /** フォルダのリネーム（未指定ならメニュー非表示） */
  onRenameFolder?: (
    // eslint-disable-next-line no-unused-vars
    node: FileBrowserFolderNode,
    // eslint-disable-next-line no-unused-vars
    newName: string,
  ) => Promise<void>;
  /** フォルダの移動（未指定ならフォルダD&Dを無効化）。parentId=null はルート */
  onMoveFolder?: (
    // eslint-disable-next-line no-unused-vars
    node: FileBrowserFolderNode,
    // eslint-disable-next-line no-unused-vars
    parentId: string | null,
  ) => Promise<void>;
  /** フォルダの削除（中身ごと。未指定ならメニュー非表示） */
  // eslint-disable-next-line no-unused-vars
  onDeleteFolder?: (node: FileBrowserFolderNode) => Promise<void>;
  /** 共有リンク機能のアダプタ（未指定なら共有メニュー非表示） */
  shareAdapter?: FileBrowserShareAdapter;
  /**
   * ディープリンク（アプリ内 URL）のコピー。渡すとファイル行・フォルダ行の
   * 3点メニューに「リンクをコピー」を追加する（コピー処理は呼び出し側）。
   * folderId はリンクを開いたときにスコープすべきフォルダ
   * （ファイルなら所属フォルダ、フォルダなら自身。null = セクションルート）。
   */
  onCopyLink?: (
    // eslint-disable-next-line no-unused-vars
    target: {
      type: "file" | "folder";
      id: string;
      name: string;
      folderId: string | null;
    },
  ) => void;
  /**
   * 選択中のオーディオを画面下のインスペクタバーに出す（サウンドライブラリ向け）。
   * 波形上のドラッグで範囲を選び、その範囲だけを書き出せるようになる。
   */
  showAudioInspector?: boolean;
  /** インスペクタバーの下端オフセット（px）。BottomAppBar 等と重なる場合に指定する */
  audioInspectorBottomOffset?: number;
  /**
   * インスペクタで表示中のファイル ID が変わったとき（閉じたら null）。
   * 呼び出し側はこれを見てブックマークの取得を開始する。
   */
  // eslint-disable-next-line no-unused-vars
  onAudioInspectorFileChange?: (fileId: string | null) => void;
  /** インスペクタに重ねるオーディオブックマーク（API 呼び出しは呼び出し側に置く） */
  audioBookmarks?: AudioInspectorBookmark[];
  /** ブックマーク作成。endSeconds=null は点のブックマーク */
  onCreateAudioBookmark?: (
    // eslint-disable-next-line no-unused-vars
    fileId: string,
    // eslint-disable-next-line no-unused-vars
    params: { startSeconds: number; endSeconds: number | null },
  ) => Promise<void>;
  /** ブックマークのラベル変更 */
  onUpdateAudioBookmark?: (
    // eslint-disable-next-line no-unused-vars
    bookmarkId: string,
    // eslint-disable-next-line no-unused-vars
    params: { label: string },
  ) => Promise<void>;
  /** ブックマーク削除 */
  // eslint-disable-next-line no-unused-vars
  onDeleteAudioBookmark?: (bookmarkId: string) => Promise<void>;
  /**
   * ブックマーク一覧ページからのジャンプ指示。requestId が変わるたびに
   * 対象ファイルを試聴選択（= インスペクタを開く）して頭出しする。
   */
  audioFocusRequest?: (AudioInspectorFocusRequest & { fileId: string }) | null;
  /** ジャンプ指示を適用し終えたとき（呼び出し側が指示をクリアする） */
  onAudioFocusRequestApplied?: () => void;
}

/**
 * 親（ツールバー / サイドバー等）から操作するための命令的ハンドル。
 * フォルダ ID は FileBrowser に渡している folders 内の ID を指定する
 * （見つからない場合は何もしない）。
 */
export interface FileBrowserHandle {
  /** フォルダ作成モーダルを開く（parentId 省略 = ルート直下） */
  // eslint-disable-next-line no-unused-vars
  openCreateFolder: (parentId?: string | null) => void;
  /** フォルダのリネームモーダルを開く */
  // eslint-disable-next-line no-unused-vars
  openRenameFolder: (folderId: string) => void;
  /** フォルダの削除確認を開く */
  // eslint-disable-next-line no-unused-vars
  openDeleteFolder: (folderId: string) => void;
  /** フォルダの共有リンクモーダルを開く */
  // eslint-disable-next-line no-unused-vars
  openShareFolder: (folderId: string) => void;
  /** フォルダを zip で一括ダウンロードする */
  // eslint-disable-next-line no-unused-vars
  downloadFolder: (folderId: string) => void;
  /** フォルダ配下の音声・動画を一括文字起こしする（enableTranscription 時のみ） */
  // eslint-disable-next-line no-unused-vars
  transcribeFolder: (folderId: string) => void;
  /** フォルダをルート（一番上の階層）へ移動する */
  // eslint-disable-next-line no-unused-vars
  moveFolderToRoot: (folderId: string) => void;
}

// ===== styled =====

/** 作成直後のフォルダ行を一瞬ハイライトするフラッシュ */
const createdRowFlash = keyframes`
  from { background-color: rgba(0, 154, 206, 0.16); }
  to { background-color: transparent; }
`;

const Container = styled.div<{ $bottomSpacing: number }>`
  width: 100%;
  /* インスペクタバーは fixed で浮くため、末尾の行が隠れないよう下に逃がす */
  padding-bottom: ${({ $bottomSpacing }) => $bottomSpacing}px;

  /* 行の background は D&D 中に inline style で上書きされるため td 側で光らせる */
  tr.folder-row-created > td {
    animation: ${createdRowFlash} 1.6s ease-out;
  }
`;

/**
 * ルート（一番上の階層）へのネイティブ D&D を受ける器。
 * 横スクロール・枠・余白は `Table` 内蔵のスクロール領域に任せ、ここは
 * ドロップ中のハイライトだけを担う。
 */
const TableWrapper = styled.div.attrs({
  className: "u-w100",
})<{ $nativeOver: boolean }>`
  border-radius: 4px;
  ${({ $nativeOver }) =>
    $nativeOver &&
    `
    box-shadow: inset 0 0 0 2px var(--soremo-blue);
    background: rgba(0, 154, 206, 0.04);
  `}
`;

const NameCellButton = styled.button.attrs({ className: "bodytext-13" })<{
  $depth: number;
  $clickable: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  /* ブックマークアバター（margin-left: auto）を列の右端へ寄せるため、
     ボタンをセル幅いっぱいに広げる（button は既定で shrink-to-fit） */
  width: 100%;
  min-width: 0;
  padding: 2px 0;
  padding-left: ${({ $depth }) => $depth * 20}px;
  border: none;
  background: transparent;
  color: inherit;
  appearance: none;
  -webkit-appearance: none;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};

  .file-name-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .material-symbols-rounded {
    font-size: 18px;
    flex-shrink: 0;
  }

  .expand-icon {
    font-size: 18px;
    transition: transform 0.15s ease;
  }

  .expand-icon.expanded {
    transform: rotate(90deg);
  }
`;

const MetaCell = styled.td.attrs({ className: "bodytext-13" })`
  color: var(--soremo-gray);
`;

const FolderCount = styled.span.attrs({ className: "bodytext-13" })`
  color: var(--soremo-light-gray);
  flex-shrink: 0;
`;

/**
 * ファイル名列の右端に出すブックマーク作成者のアバター（重ね表示）。
 * margin-left: auto で名前・バッジより右端へ寄せる。
 */
const BookmarkAvatarStack = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  padding-right: 2px;
`;

/** 重なりを見せるための白フチ付きアバターチップ */
const BookmarkAvatarChip = styled.span`
  display: inline-flex;
  border-radius: 50%;
  border: 1.5px solid #fff;
  background: #fff;
  line-height: 0;

  & + & {
    margin-left: -6px;
  }
`;

const ActionsCell = styled.td`
  /* DropdownMenu(Tippy) がセル内でクリップされないようにする */
  overflow: visible !important;
  text-align: center;
`;

const EmptyState = styled.div.attrs({ className: "u-col-center u-w100" })`
  padding: 48px 0;
`;

/**
 * テーブル末尾の「+ ファイルを追加」行（ショット編集の MediaItemTable と同じ
 * 統一アップロード導線）。クリックでファイル選択ダイアログを開く。
 */
const AddFileRowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 4px 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--soremo-light-gray);
  transition: color 0.2s;

  .material-symbols-rounded {
    font-size: 18px;
    color: inherit;
  }

  &:hover {
    color: var(--soremo-blue);
  }
`;

const RenameInput = styled.input.attrs({ type: "text" })`
  width: 100%;
`;

// D&D 中にカーソルへ追従するゴースト表示
const DragGhost = styled.div.attrs({ className: "bodytext-13" })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: #fff;
  border: 1px solid var(--soremo-blue);
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  white-space: nowrap;

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

/**
 * テーブル + ルートドロップバーの位置決めコンテナ。
 * バーはドラッグ開始後にマウントされるため、通常フローに挿入すると
 * レイアウトがずれて dnd-kit がドラッグ開始時に測定した座標と食い違う。
 * オーバーレイ（absolute）にすることでレイアウトシフトを起こさない。
 */
const DndTableArea = styled.div`
  position: relative;
`;

/**
 * テーブル内 D&D 中にだけ現れる「ルート（一番上の階層）へ移動」のドロップ受け。
 * フォルダ行以外に置き場が無いと第二階層 → 第一階層の移動ができないため、
 * ドラッグ中はテーブル上部（ヘッダー行の上）へオーバーレイ表示する。
 */
const RootDropBar = styled.div<{ $active: boolean; $ready: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  /* グレーの破線 = まだ離しても移動しない / 青の実線 = ここで離せば移動する */
  border: 2px ${({ $ready }) => ($ready ? "solid" : "dashed")}
    ${({ $ready }) => ($ready ? "var(--soremo-blue)" : DROP_DWELL_LINE)};
  border-radius: 6px;
  /* フォルダ行と同じく、滞留の進み具合を塗りの面積で見せる */
  background-color: rgba(255, 255, 255, 0.95);
  background-image: ${({ $ready }) => {
    const fill = $ready ? DROP_READY_FILL : DROP_DWELL_FILL;
    return `linear-gradient(${fill}, ${fill})`;
  }};
  background-repeat: no-repeat;
  background-size: ${({ $active }) => ($active ? "100% 100%" : "0 100%")};
  color: ${({ $ready }) =>
    $ready ? "var(--soremo-blue)" : "var(--soremo-gray)"};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition:
    background-size
      ${({ $active, $ready }) =>
        $active && !$ready ? `${DROP_DWELL_MS}ms linear` : "120ms ease-out"},
    border-color 0.15s ease,
    color 0.15s ease;

  .material-symbols-rounded {
    font-size: 18px;
  }
`;

/** dnd-kit のルートドロップ先 ID */
const ROOT_DROP_ID = "root-drop";

const RootMoveDropZone: React.FC = () => {
  const { setNodeRef, isOver } = useDroppable({ id: ROOT_DROP_ID });
  const dwellReady = useDropDwellReady(isOver);
  return (
    <RootDropBar ref={setNodeRef} $active={isOver} $ready={dwellReady}>
      <span className="material-symbols-rounded">move_up</span>
      <span className="bodytext-13">
        ここにドロップでルート（一番上の階層）へ移動
      </span>
    </RootDropBar>
  );
};

// ===== 表示行（ツリーをフラット化した描画用リスト） =====

type DisplayRow =
  | {
      kind: "folder";
      node: FileBrowserFolderNode;
      /** 表示上のインデント段数。検索中はヒットしたフォルダを起点にした相対値 */
      depth: number;
    }
  | { kind: "file"; file: FileBrowserFile; depth: number }
  /** 遅延ロード中フォルダの直下に出すローディング行 */
  | { kind: "loading"; folderId: string; depth: number };

/**
 * ツリーを展開状態に従って表示行へフラット化する。
 * `depthBase` を渡すと、そのぶんインデントを差し引いた相対深さで行を出す
 * （検索ヒットしたフォルダを起点に部分ツリーを表示するために使う）。
 */
const flattenForDisplay = (
  rootFolders: FileBrowserFolderNode[],
  rootFiles: FileBrowserFile[],
  expandedIds: Set<string>,
  loadedFolderIds?: ReadonlySet<string>,
  depthBase = 0,
): DisplayRow[] => {
  const result: DisplayRow[] = [];
  const walk = (node: FileBrowserFolderNode): void => {
    result.push({ kind: "folder", node, depth: node.depth - depthBase });
    if (!expandedIds.has(node.id)) return;
    node.children.forEach(walk);
    // 直下ファイルが未ロード（遅延ロード中）の間はローディング行を出す
    if (loadedFolderIds && !loadedFolderIds.has(node.id)) {
      result.push({
        kind: "loading",
        folderId: node.id,
        depth: node.depth + 1 - depthBase,
      });
      return;
    }
    node.files.forEach((file) => {
      result.push({ kind: "file", file, depth: node.depth + 1 - depthBase });
    });
  };
  rootFolders.forEach(walk);
  rootFiles.forEach((file) => {
    result.push({ kind: "file", file, depth: 0 });
  });
  return result;
};

const PREVIEWABLE_TYPES = ["image", "video", "audio", "document"];

const toMediaType = (fileType: string): MediaType =>
  (fileType === "document" ? "pdf" : fileType) as MediaType;

// ===== D&D =====

/** ドラッグ中のアイテム（ファイル or フォルダ） */
type ActiveDrag =
  | { kind: "file"; file: FileBrowserFile }
  | {
      kind: "folder";
      node: FileBrowserFolderNode;
      /** 自分自身と配下の全フォルダID（循環防止のドロップ不可判定用） */
      excludedIds: Set<string>;
    };

/** ドラッグ中アイテムをこのフォルダへドロップできるか */
const isValidDropTarget = (
  drag: ActiveDrag | null,
  node: FileBrowserFolderNode,
): boolean => {
  if (!drag || !node.canEdit) return false;
  if (drag.kind === "file") {
    return drag.file.canEdit !== false && drag.file.folderId !== node.id;
  }
  return (
    drag.node.canEdit &&
    !drag.excludedIds.has(node.id) &&
    drag.node.parentId !== node.id
  );
};

/**
 * ファイル行（ドラッグ元）。
 * オーディオ行は試聴（audition）の行選択・キーボード操作 props を合成する。
 */
const DraggableFileTr: React.FC<{
  file: FileBrowserFile;
  draggable: boolean;
  auditionRowProps?: AuditionRowProps;
  auditionSelected?: boolean;
  children: React.ReactNode;
}> = ({ file, draggable, auditionRowProps, auditionSelected, children }) => {
  const dragData = useMemo<ActiveDrag>(() => ({ kind: "file", file }), [file]);
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `file:${file.id}`,
    data: dragData,
    disabled: !draggable,
  });
  const {
    ref: auditionRef,
    style: auditionStyle,
    ...auditionRest
  } = auditionRowProps ?? {};
  return (
    <tr
      ref={(el) => {
        setNodeRef(el);
        auditionRef?.(el);
      }}
      {...listeners}
      {...attributes}
      {...auditionRest}
      style={{
        opacity: isDragging ? 0.4 : 1,
        // タッチは長押しでドラッグを開始するため、待機中のスクロールは
        // ブラウザに任せる（"none" にするとこの行から一覧をスクロールできない）。
        touchAction: draggable ? "manipulation" : undefined,
        // 長押し中に iOS の選択メニューが出るのを抑える
        WebkitTouchCallout: draggable ? "none" : undefined,
        background: auditionSelected ? "rgba(0, 154, 206, 0.06)" : undefined,
        ...auditionStyle,
      }}
    >
      {children}
    </tr>
  );
};

/**
 * フォルダ行（ドラッグ元 + ドロップ先）。
 * - テーブル内D&D: ファイル・フォルダの移動先として受け付ける（循環は除外）
 * - 外部D&D: OSからのファイル / フォルダドロップでこのフォルダへアップロードする
 */
const FolderTr: React.FC<{
  node: FileBrowserFolderNode;
  draggable: boolean;
  activeDrag: ActiveDrag | null;
  // eslint-disable-next-line no-unused-vars
  onNativeFileDrop?: (entries: FileWithPath[]) => void;
  children: React.ReactNode;
}> = ({ node, draggable, activeDrag, onNativeFileDrop, children }) => {
  const validTarget = isValidDropTarget(activeDrag, node);
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `folder-drop:${node.id}`,
    data: { node },
    disabled: !validTarget,
  });
  // 配下フォルダ ID の収集はサブツリー全体の走査になるため、レンダーごとに
  // 再計算せずノード参照が変わったときだけ行う（多数のフォルダ行が並ぶ
  // 検索時・大規模ライブラリでの負荷対策）。
  const excludedIds = useMemo(() => collectFolderNodeIds(node), [node]);
  const dragData = useMemo<ActiveDrag>(
    () => ({ kind: "folder", node, excludedIds }),
    [node, excludedIds],
  );
  const {
    setNodeRef: setDragRef,
    listeners,
    attributes,
    isDragging,
  } = useDraggable({
    id: `folder:${node.id}`,
    data: dragData,
    disabled: !draggable,
  });

  // 外部（OS）ファイルドラッグのホバー状態
  const [isNativeOver, setIsNativeOver] = useState(false);

  const isExternalFileDrag = (e: React.DragEvent): boolean =>
    !!onNativeFileDrop && containsNativeFiles(e);

  const handleNativeDragOver = (e: React.DragEvent) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsNativeOver(true);
  };

  const handleNativeDrop = (e: React.DragEvent) => {
    if (!isExternalFileDrag(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setIsNativeOver(false);
    void extractDropEntries(e).then((entries) => {
      if (entries.length > 0) onNativeFileDrop!(entries);
    });
  };

  const dndOver = validTarget && isOver;
  // 滞留時間を要求するのはテーブル内 D&D（移動）だけ。OS からのドロップは
  // アップロードなので、誤って移動が確定してしまう問題は起きない。
  const dwellReady = useDropDwellReady(dndOver);
  const dropStyle =
    validTarget || isNativeOver
      ? dropTargetRowStyle({
          isOver: dndOver || isNativeOver,
          isReady: dwellReady || isNativeOver,
        })
      : null;

  return (
    <tr
      ref={(el) => {
        setDropRef(el);
        setDragRef(el);
      }}
      data-folder-row-id={node.id}
      {...listeners}
      {...attributes}
      onDragOver={handleNativeDragOver}
      onDragLeave={() => setIsNativeOver(false)}
      onDrop={handleNativeDrop}
      style={{
        opacity: isDragging ? 0.4 : 1,
        // DraggableFileTr と同じ理由でスクロールを潰さない（長押しで開始する）
        touchAction: draggable ? "manipulation" : undefined,
        WebkitTouchCallout: draggable ? "none" : undefined,
        ...dropStyle,
      }}
    >
      {children}
    </tr>
  );
};

// ===== 行コンポーネント（メモ化） =====
//
// 行数が数百〜数千になり得るため、行は React.memo で囲み、親（FileBrowser）の
// 再レンダー（検索テキストのタイプ・試聴の再生位置 RAF 更新など）で全行が
// 再レンダーされないようにする。親から渡す props は安定参照のみにすること。

/** フォルダ行（行全体 = FolderTr + セル群） */
const FolderRow = React.memo<{
  node: FileBrowserFolderNode;
  /** 表示上のインデント段数（検索中はヒットしたフォルダを起点にした相対値） */
  depth: number;
  isExpanded: boolean;
  draggable: boolean;
  activeDrag: ActiveDrag | null;
  /** 外部（OS）D&D アップロードを受け付けるか */
  canNativeDrop: boolean;
  onToggle: (_folderId: string) => void;
  onExternalDrop: (_folderId: string | null, _entries: FileWithPath[]) => void;
  buildMenuItems: (_node: FileBrowserFolderNode) => DropdownMenuItem[];
}>(
  ({
    node,
    depth,
    isExpanded,
    draggable,
    activeDrag,
    canNativeDrop,
    onToggle,
    onExternalDrop,
    buildMenuItems,
  }) => (
    <FolderTr
      node={node}
      draggable={draggable}
      activeDrag={activeDrag}
      onNativeFileDrop={
        canNativeDrop ? (entries) => onExternalDrop(node.id, entries) : undefined
      }
    >
      <td>
        <NameCellButton
          type="button"
          $depth={depth}
          $clickable
          onClick={() => onToggle(node.id)}
        >
          <span
            className={`material-symbols-rounded expand-icon${isExpanded ? " expanded" : ""}`}
          >
            chevron_right
          </span>
          <span className="file-name-text">{node.name}</span>
          <FolderCount>{node.totalCount}件</FolderCount>
          {/* 進行中モーダルの「旧環境」chip と同じデザイン */}
          {node.isShared && (
            <StatusBadge label="共有中" size="small" color="border" />
          )}
        </NameCellButton>
      </td>
      <MetaCell>{formatDateTime(node.latestUpdated)}</MetaCell>
      <MetaCell>{formatFileSize(node.totalSize)}</MetaCell>
      <ActionsCell>
        <DropdownMenu items={buildMenuItems(node)} />
      </ActionsCell>
    </FolderTr>
  ),
);
FolderRow.displayName = "FolderRow";

/** 試聴行のフォーカスリング打ち消し（useAudioAudition.getRowProps と同一） */
const AUDITION_ROW_STYLE: React.CSSProperties = { outline: "none" };

/** ファイル行（行全体 = DraggableFileTr + セル群） */
const FileRow = React.memo<{
  file: FileBrowserFile;
  depth: number;
  draggable: boolean;
  auditionSelected: boolean;
  auditionPlaying: boolean;
  onPreview: (_file: FileBrowserFile) => void;
  onAuditionToggle: (_key: string) => void;
  onAuditionRowRef: (_key: string, _el: HTMLElement | null) => void;
  onAuditionRowClick: (_key: string) => void;
  onAuditionRowKeyDown: (
    _key: string,
    _e: React.KeyboardEvent<HTMLElement>,
  ) => void;
  buildMenuItems: (_file: FileBrowserFile) => DropdownMenuItem[];
  /** 文字起こしステータス / 本文プレビュー（未実行なら undefined で非表示） */
  transcriptionStatus?: TranscriptionCellInfo;
}>(
  ({
    file,
    depth,
    draggable,
    auditionSelected,
    auditionPlaying,
    onPreview,
    onAuditionToggle,
    onAuditionRowRef,
    onAuditionRowClick,
    onAuditionRowKeyDown,
    buildMenuItems,
    transcriptionStatus,
  }) => {
    const isAudio = file.fileType === "audio";
    const hasSrc = !!(file.optimizedUrl || file.originalUrl);
    // オーディオは行クリック = 試聴選択（プレビューはメニューから）
    const isAuditionable = isAudio && hasSrc;
    const isPreviewable =
      !isAudio && PREVIEWABLE_TYPES.includes(file.fileType) && hasSrc;
    // useAudioAudition.getRowProps 相当の行 props を安定コールバックから組み立てる
    // （getRowProps 本体は再レンダーごとに参照が変わり、memo を無効化するため）
    const auditionRowProps: AuditionRowProps | undefined = isAuditionable
      ? {
          ref: (el) => onAuditionRowRef(file.id, el),
          tabIndex: auditionSelected ? 0 : -1,
          onClick: () => onAuditionRowClick(file.id),
          onKeyDown: (e) => onAuditionRowKeyDown(file.id, e),
          style: AUDITION_ROW_STYLE,
        }
      : undefined;
    return (
      <DraggableFileTr
        file={file}
        draggable={draggable}
        auditionRowProps={auditionRowProps}
        auditionSelected={isAuditionable && auditionSelected}
      >
        <td>
          <NameCellButton
            type="button"
            $depth={depth}
            $clickable={isPreviewable}
            onClick={() => isPreviewable && onPreview(file)}
          >
            {isAuditionable ? (
              <span
                className="material-symbols-rounded"
                style={{
                  fontSize: 20,
                  color: auditionPlaying
                    ? "var(--soremo-blue)"
                    : "var(--soremo-light-gray)",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onAuditionToggle(file.id);
                }}
              >
                {auditionPlaying ? "pause_circle" : "play_circle"}
              </span>
            ) : (
              <span className="material-symbols-rounded">
                {getFileIconFromCategoryOrExtension(file.fileType, file.name)}
              </span>
            )}
            <span className="file-name-text">{file.name}</span>
            {/* 進行中モーダルの「旧環境」chip と同じデザイン */}
            {file.isShared && (
              <StatusBadge label="共有中" size="small" color="border" />
            )}
            <TranscriptionStatusCell entry={transcriptionStatus} />
            {/* ブックマークがあるファイルは作成者アバターを列の右端に出す */}
            {(file.bookmarkUsers?.length ?? 0) > 0 && (
              <BookmarkAvatarStack
                aria-label={`ブックマーク ${file.bookmarkCount ?? 0} 件`}
              >
                {file.bookmarkUsers!.slice(0, 3).map((user) => (
                  <BookmarkAvatarChip key={user.id}>
                    <Avatar
                      size={16}
                      src={user.avatar ?? undefined}
                      name={user.name ?? undefined}
                    />
                  </BookmarkAvatarChip>
                ))}
                {file.bookmarkUsers!.length > 3 && (
                  <span
                    className="bodytext-11"
                    style={{
                      color: "var(--soremo-light-gray)",
                      marginLeft: 2,
                    }}
                  >
                    +{file.bookmarkUsers!.length - 3}
                  </span>
                )}
              </BookmarkAvatarStack>
            )}
          </NameCellButton>
        </td>
        <MetaCell>{formatDateTime(file.updatedAt)}</MetaCell>
        <MetaCell>{formatFileSize(file.sizeBytes)}</MetaCell>
        <ActionsCell>
          <DropdownMenu items={buildMenuItems(file)} />
        </ActionsCell>
      </DraggableFileTr>
    );
  },
);
FileRow.displayName = "FileRow";

/**
 * 汎用ファイルブラウザ（グローバルコンポーネント）。
 *
 * トークルームのストレージビュー（TalkRoomFileTable）を土台に、トークルーム
 * 固有の要素（受領/送付タグ・陣営判定・メッセージ紐付け・ACR・DL既読アバター）を
 * 除いて props 駆動に汎用化したもの。データ取得・更新はすべてコールバックで
 * 注入し、コンポーネント自体はドメイン API に依存しない。
 *
 * - フォルダ階層を展開/折りたたみ可能なツリー行で表現（ファイル名クリックでプレビュー）
 * - 列ヘッダークリックでソート（ファイル名 / 更新日 / サイズ）
 * - searchText でフォルダ名・ファイル名を絞り込み。検索中は階層を出さず、
 *   ヒットした行だけのフラットリストを表示する（大規模ライブラリ対策）
 * - ファイル行の3点リーダー: ダウンロード / 共有 / リネーム / 差し替え / ルートへ移動 / 削除
 * - フォルダ行の3点リーダー: 一括DL(zip) / 共有 / フォルダ作成 / リネーム / ルートへ移動 / 削除
 * - D&D: テーブル内でファイル・フォルダの移動（循環は禁止）、OSからのドロップでアップロード
 *   （フォルダ行 = そのフォルダへ、テーブル余白・末尾の「ファイルを追加」行 = ルート
 *   （表示中の階層）へ。フォルダごとのドロップは webkitGetAsEntry で再帰走査し、
 *   relativePath 付きで onUploadFiles へ渡す）
 * - 共有中のファイル / フォルダには「共有中」バッジを表示
 */
const FileBrowser = forwardRef<FileBrowserHandle, FileBrowserProps>(
  (
    {
      files,
      folders,
      searchText = "",
      isLoading = false,
      folderStats,
      loadedFolderIds,
      onExpandFolder,
      resolveZipEntries,
      enableTranscription = false,
      resolveFolderFiles,
      emptyMessage = "アップロードされたファイルはまだありません",
      canEditRoot = false,
      onFileDownloaded,
      onRenameFile,
      onMoveFile,
      onReplaceFile,
      onDeleteFile,
      onUploadFiles,
      onCreateFolder,
      onRenameFolder,
      onMoveFolder,
      onDeleteFolder,
      shareAdapter,
      onCopyLink,
      showAudioInspector = false,
      audioInspectorBottomOffset = 0,
      onAudioInspectorFileChange,
      audioBookmarks,
      onCreateAudioBookmark,
      onUpdateAudioBookmark,
      onDeleteAudioBookmark,
      audioFocusRequest,
      onAudioFocusRequestApplied,
    },
    ref,
  ) => {
    const [sortState, setSortState] = useState<FileBrowserSortState>({
      field: "updated",
      order: "desc",
    });
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
      new Set(),
    );
    // toggleFolder（メモ化済み）から「開く操作かどうか」を判定するための参照。
    // deps に expandedFolders を入れると全フォルダ行が展開のたびに再レンダー
    // されるため ref で読む。
    const expandedFoldersRef = useRef(expandedFolders);
    useEffect(() => {
      expandedFoldersRef.current = expandedFolders;
    }, [expandedFolders]);
    const [previewFile, setPreviewFile] = useState<FileBrowserFile | null>(
      null,
    );
    const [renameFileTarget, setRenameFileTarget] =
      useState<FileBrowserFile | null>(null);
    const [renameFolderTarget, setRenameFolderTarget] =
      useState<FileBrowserFolderNode | null>(null);
    const [renameDraft, setRenameDraft] = useState("");
    const [isRenaming, setIsRenaming] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [deleteFileTarget, setDeleteFileTarget] =
      useState<FileBrowserFile | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [activeDrag, setActiveDrag] = useState<ActiveDrag | null>(null);
    // フォルダ作成モーダル（作成先の親。null = ルート直下）
    const [createFolderState, setCreateFolderState] = useState<{
      parentId: string | null;
      parentName: string | null;
    } | null>(null);
    const [createFolderName, setCreateFolderName] = useState("");
    const [isCreatingFolder, setIsCreatingFolder] = useState(false);
    // 作成直後にスクロール + ハイライトで見せるフォルダ（行の出現を待つ）
    const [highlightFolderId, setHighlightFolderId] = useState<string | null>(
      null,
    );
    const containerRef = useRef<HTMLDivElement>(null);
    // フォルダ削除確認
    const [deleteFolderTarget, setDeleteFolderTarget] =
      useState<FileBrowserFolderNode | null>(null);
    const [isDeletingFolder, setIsDeletingFolder] = useState(false);
    // フォルダ移動確認（配下ごと動くため、実行前に必ず挟む）。
    // parentId は移動先（null = ルート）、label は表示用の移動先名。
    const [moveFolderTarget, setMoveFolderTarget] = useState<{
      node: FileBrowserFolderNode;
      parentId: string | null;
      label: string;
    } | null>(null);
    const [isMovingFolder, setIsMovingFolder] = useState(false);
    // 外部D&Dアップロード中フラグ
    const [isUploading, setIsUploading] = useState(false);
    // テーブル余白（ルート）への外部D&Dホバー状態（子要素との出入りはカウンタで判定）
    const [isRootNativeOver, setIsRootNativeOver] = useState(false);
    const rootDragDepthRef = useRef(0);
    // 末尾「+ ファイルを追加」行への外部D&Dホバー状態
    const [isAddRowNativeOver, setIsAddRowNativeOver] = useState(false);
    // 共有リンクモーダルの対象
    const [shareTarget, setShareTarget] =
      useState<FileBrowserShareTarget | null>(null);

    const replaceTargetRef = useRef<FileBrowserFile | null>(null);
    const replaceInputRef = useRef<HTMLInputElement>(null);

    // テーブル内D&D（移動）用センサー。行全体がドラッグ元で、同じ行に
    // フォルダの開閉・ファイルの試聴選択のクリックが乗っているため、
    // 入力手段ごとにクリック / タップ / スクロールと区別するしきい値を置く。
    // - マウス: 12px 動かしてからドラッグ開始（クリック中の手ぶれを拾わない）
    // - タッチ: 250ms 長押ししてから開始。待機中に 5px 動いたらスクロール意図
    //   として起動を取り消す
    const dndSensors = useSensors(
      useSensor(MouseSensor, { activationConstraint: { distance: 12 } }),
      useSensor(TouchSensor, {
        activationConstraint: { delay: 250, tolerance: 5 },
      }),
    );

    // ===== ツリー構築 =====

    // 検索テキストは2段階で遅延反映する（膨大なライブラリでのフリーズ対策）。
    // 1. debounce: 1文字ごとの中間値で重い再計算が走らないようにする
    // 2. useDeferredValue: 反映時の再計算・再描画を低優先レンダーに逃がし、
    //    タイピング（urgent update）をブロックしない
    const debouncedSearchText = useDebouncedValue(searchText, 200);
    const deferredSearchText = useDeferredValue(debouncedSearchText);
    const searchQuery = deferredSearchText.trim().toLowerCase();

    // 全件のソート済みツリー。検索テキストを依存に含めず、検索のたびに
    // O(全件) のツリー再構築・集計・ソートが走らないようにする。
    const sortedTree = useMemo(() => {
      const { rootFolders, rootFiles } = buildFileBrowserTree(
        files,
        folders,
        folderStats,
      );
      sortFileBrowserTree(rootFolders, sortState);
      return {
        rootFolders,
        rootFiles: sortFileBrowserFiles(rootFiles, sortState),
      };
    }, [files, folders, folderStats, sortState]);

    const displayRows = useMemo(() => {
      // 通常表示: ツリーを展開状態に従ってフラット化
      if (!searchQuery) {
        return flattenForDisplay(
          sortedTree.rootFolders,
          sortedTree.rootFiles,
          expandedFolders,
          loadedFolderIds,
        );
      }
      // 検索中: 祖先階層の復元はせず、名前がヒットしたフォルダとファイルを
      // フラットに表示する（以前の「全フォルダ展開」は膨大なライブラリで
      // 数千行の DOM を毎回描画し、フリーズの主因になっていた）。
      // ヒットしたフォルダはそこを起点にした部分ツリーとして描画し、
      // 通常表示と同じくクリックで展開できる（自動展開はしない）。
      const rows: DisplayRow[] = [];
      // ヒットフォルダの展開サブツリーとして既に描画した行の重複を防ぐ。
      // walkFolders は親→子の順（pre-order）なので、先に出た祖先の
      // サブツリーに含まれる子孫ヒットはここで除外できる。
      const renderedFolderIds = new Set<string>();
      const renderedFileIds = new Set<string>();
      const walkFolders = (nodes: FileBrowserFolderNode[]): void => {
        nodes.forEach((node) => {
          if (
            node.name.toLowerCase().includes(searchQuery) &&
            !renderedFolderIds.has(node.id)
          ) {
            const subtree = flattenForDisplay(
              [node],
              [],
              expandedFolders,
              loadedFolderIds,
              node.depth,
            );
            subtree.forEach((row) => {
              if (row.kind === "folder") renderedFolderIds.add(row.node.id);
              else if (row.kind === "file") renderedFileIds.add(row.file.id);
            });
            rows.push(...subtree);
          }
          walkFolders(node.children);
        });
      };
      walkFolders(sortedTree.rootFolders);
      const matchedFiles = sortFileBrowserFiles(
        files.filter(
          (f) =>
            f.name.toLowerCase().includes(searchQuery) &&
            !renderedFileIds.has(f.id),
        ),
        sortState,
      );
      matchedFiles.forEach((file) => {
        rows.push({ kind: "file", file, depth: 0 });
      });
      return rows;
    }, [
      sortedTree,
      files,
      sortState,
      expandedFolders,
      searchQuery,
      loadedFolderIds,
    ]);

    // 作成直後のフォルダ行が（キャッシュ反映の再レンダー後に）DOM へ現れたら
    // スクロールして一瞬ハイライトする。検索中で行が出ない場合などは
    // 次の作成時に上書きされるだけで実害はない。
    useEffect(() => {
      if (!highlightFolderId) return;
      const row = containerRef.current?.querySelector<HTMLElement>(
        `[data-folder-row-id="${highlightFolderId}"]`,
      );
      if (!row) return;
      setHighlightFolderId(null);
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
      row.classList.add("folder-row-created");
      window.setTimeout(
        () => row.classList.remove("folder-row-created"),
        1600,
      );
    }, [highlightFolderId, displayRows]);

    // 検索フィルタと無関係な全ツリーの folderId → ノード表。
    // 命令的ハンドル（サイドバーの3点メニュー等）から ID 指定でフォルダ操作
    // モーダルを開くために使う。
    const folderNodesById = useMemo(() => {
      const map = new Map<string, FileBrowserFolderNode>();
      const walk = (nodes: FileBrowserFolderNode[]): void => {
        nodes.forEach((node) => {
          map.set(node.id, node);
          walk(node.children);
        });
      };
      walk(sortedTree.rootFolders);
      return map;
    }, [sortedTree]);

    // 表示中のオーディオ行を表示順に集めて試聴対象にする（行選択 + ↑↓ + Space）
    const audioAuditionItems = useMemo<AuditionItem[]>(() => {
      const items: AuditionItem[] = [];
      displayRows.forEach((entry) => {
        if (entry.kind !== "file" || entry.file.fileType !== "audio") return;
        const src = entry.file.optimizedUrl || entry.file.originalUrl;
        if (src) items.push({ key: entry.file.id, src });
      });
      return items;
    }, [displayRows]);

    const audition = useAudioAudition(audioAuditionItems);

    // audition の API（getRowProps 等）は再生位置の RAF 更新のたびに参照が
    // 変わるため、メモ化した FileRow へは ref 経由の安定したラッパーを渡す
    // （再生中に全行が毎フレーム再レンダーされるのを防ぐ）。
    const auditionRef = useRef(audition);
    useEffect(() => {
      auditionRef.current = audition;
    });
    const handleAuditionRowRef = useCallback(
      (key: string, el: HTMLElement | null) => {
        auditionRef.current.getRowProps(key).ref(el);
      },
      [],
    );
    const handleAuditionRowClick = useCallback((key: string) => {
      auditionRef.current.select(key);
    }, []);
    const handleAuditionRowKeyDown = useCallback(
      (key: string, e: React.KeyboardEvent<HTMLElement>) => {
        auditionRef.current.getRowProps(key).onKeyDown(e);
      },
      [],
    );
    const handleAuditionToggle = useCallback((key: string) => {
      auditionRef.current.toggle(key);
    }, []);

    // ===== オーディオインスペクタ（サウンドライブラリ向け） =====

    // 閉じるボタンで一旦引っ込め、別のオーディオを選び直したらまた出す
    const [inspectorDismissed, setInspectorDismissed] = useState(false);
    useEffect(() => {
      setInspectorDismissed(false);
    }, [audition.selectedKey]);

    const inspectorFile = useMemo(() => {
      if (!showAudioInspector || inspectorDismissed || !audition.selectedKey) {
        return null;
      }
      const target = files.find(
        (f) => f.id === audition.selectedKey && f.fileType === "audio",
      );
      if (!target?.originalUrl) return null;
      return {
        id: target.id,
        name: target.name,
        originalUrl: target.originalUrl,
        optimizedUrl: target.optimizedUrl,
        waveformUrl: target.waveformUrl,
      };
    }, [showAudioInspector, inspectorDismissed, audition.selectedKey, files]);

    // インスペクタの対象ファイルを呼び出し側へ通知する（ブックマーク取得の起点）。
    // コールバックの参照変化で通知が再発火しないよう ref 経由にする。
    const inspectorFileChangeRef = useRef(onAudioInspectorFileChange);
    inspectorFileChangeRef.current = onAudioInspectorFileChange;
    const inspectorFileId = inspectorFile?.id ?? null;
    useEffect(() => {
      inspectorFileChangeRef.current?.(inspectorFileId);
    }, [inspectorFileId]);

    // ブックマーク一覧ページからのジャンプ: 対象ファイルを試聴選択して
    // インスペクタを開く（頭出し・範囲復元はインスペクタ側の focusRequest）。
    const auditionSelectRef = useRef(audition.select);
    auditionSelectRef.current = audition.select;
    // 文字起こしモーダルからの頭出しにも使う
    const auditionSeekToRef = useRef(audition.seekTo);
    auditionSeekToRef.current = audition.seekTo;
    const audioFocusFileId = audioFocusRequest?.fileId ?? null;
    const audioFocusRequestId = audioFocusRequest?.requestId ?? 0;
    useEffect(() => {
      if (!audioFocusFileId || !audioFocusRequestId) return;
      const target = files.find(
        (f) => f.id === audioFocusFileId && f.fileType === "audio",
      );
      if (target) auditionSelectRef.current(audioFocusFileId);
    }, [audioFocusFileId, audioFocusRequestId, files]);

    // ===== zip 一括ダウンロード =====

    const [downloadingFolderName, setDownloadingFolderName] = useState("");
    const {
      downloadState,
      isDownloading: isBulkDownloading,
      startDownload,
      closeDownloadModal,
    } = useBulkDownload(onFileDownloaded);

    const handleFolderDownload = useCallback(
      (node: FileBrowserFolderNode) => {
        setDownloadingFolderName(node.name);
        // 保存先ダイアログはクリック直後でないと開けないため、await を挟まずに渡す。
        // 遅延ロード時は手元の files が一部なので、配下全件の解決（と失効した URL の
        // 取り直し）を resolver ごと useBulkDownload に任せる。
        startDownload(
          resolveZipEntries
            ? () => resolveZipEntries(node)
            : flattenFolderNodeToZipEntries(node),
          node.name,
        );
      },
      [startDownload, resolveZipEntries],
    );

    // ===== 文字起こし =====

    // 単体実行モーダルの対象（音声・動画）
    const [transcriptionFile, setTranscriptionFile] =
      useState<FileBrowserFile | null>(null);
    // フォルダ単位の一括実行モーダル
    const [bulkTranscription, setBulkTranscription] = useState<{
      targets: BulkTranscriptionTarget[];
      scopeLabel: string;
    } | null>(null);
    const [isResolvingTranscribeTargets, setIsResolvingTranscribeTargets] =
      useState(false);

    // ステータスチップ用の購読。音声・動画だけを対象にし、実行中が残っている
    // 間はフックがポーリングして一覧を自動更新する。
    const transcribableFileIds = useMemo(
      () =>
        enableTranscription
          ? files
              .filter((f) => isTranscribableFileType(f.fileType))
              .map((f) => f.id)
          : [],
      [enableTranscription, files],
    );
    const transcriptionStatuses = useTranscriptionStatuses(
      transcribableFileIds,
      !enableTranscription,
    );

    const handleFolderTranscribe = useCallback(
      async (node: FileBrowserFolderNode) => {
        setIsResolvingTranscribeTargets(true);
        let candidates: FileBrowserFile[];
        try {
          // 遅延ロード時: 手元の files は一部のため、配下全件を API で解決
          candidates = resolveFolderFiles
            ? await resolveFolderFiles(node)
            : flattenFolderNodeToFiles(node);
        } catch {
          toast.error("文字起こし対象の取得に失敗しました");
          return;
        } finally {
          setIsResolvingTranscribeTargets(false);
        }
        const targets = collectTranscriptionTargets(candidates, (file) => ({
          fileId: file.id,
          fileType: file.fileType,
        }));
        if (targets.length === 0) {
          toast.error("このフォルダに音声・動画ファイルがありません");
          return;
        }
        setBulkTranscription({
          targets,
          scopeLabel: `フォルダ「${node.name}」`,
        });
      },
      [resolveFolderFiles],
    );

    // ===== 基本ハンドラー =====

    const handleSort = useCallback((field: FileBrowserSortField) => {
      setSortState((prev) => ({
        field,
        order: prev.field === field && prev.order === "asc" ? "desc" : "asc",
      }));
    }, []);

    const toggleFolder = useCallback(
      (folderId: string) => {
        const willExpand = !expandedFoldersRef.current.has(folderId);
        setExpandedFolders((prev) => {
          const next = new Set(prev);
          if (next.has(folderId)) {
            next.delete(folderId);
          } else {
            next.add(folderId);
          }
          return next;
        });
        // 開く操作のときだけ遅延ロードのトリガーを親へ通知
        if (willExpand) onExpandFolder?.(folderId);
      },
      [onExpandFolder],
    );

    const expandFolder = useCallback(
      (folderId: string | null) => {
        if (!folderId) return;
        setExpandedFolders((prev) => new Set([...prev, folderId]));
        onExpandFolder?.(folderId);
      },
      [onExpandFolder],
    );

    const handleDownload = useCallback(
      (file: FileBrowserFile) => {
        downloadFile(file.originalUrl, file.name);
        onFileDownloaded?.(file.id);
      },
      [onFileDownloaded],
    );

    // ===== リネーム（ファイル / フォルダ共通モーダル） =====

    const openRenameFile = useCallback((file: FileBrowserFile) => {
      setRenameFileTarget(file);
      setRenameDraft(file.name);
    }, []);

    const openRenameFolder = useCallback((node: FileBrowserFolderNode) => {
      setRenameFolderTarget(node);
      setRenameDraft(node.name);
    }, []);

    const closeRename = useCallback(() => {
      setRenameFileTarget(null);
      setRenameFolderTarget(null);
    }, []);

    const handleRenameConfirm = useCallback(async () => {
      if (isRenaming) return;
      const newName = renameDraft.trim();
      if (!newName) return;
      if (renameFileTarget) {
        if (newName === renameFileTarget.name) {
          closeRename();
          return;
        }
        setIsRenaming(true);
        try {
          await onRenameFile?.(renameFileTarget, newName);
          toast.success("ファイル名を変更しました");
          closeRename();
        } catch {
          toast.error("ファイル名の変更に失敗しました");
        } finally {
          setIsRenaming(false);
        }
        return;
      }
      if (renameFolderTarget) {
        if (newName === renameFolderTarget.name) {
          closeRename();
          return;
        }
        setIsRenaming(true);
        try {
          await onRenameFolder?.(renameFolderTarget, newName);
          toast.success("フォルダ名を変更しました");
          closeRename();
        } catch {
          toast.error("フォルダ名の変更に失敗しました");
        } finally {
          setIsRenaming(false);
        }
      }
    }, [
      isRenaming,
      renameDraft,
      renameFileTarget,
      renameFolderTarget,
      onRenameFile,
      onRenameFolder,
      closeRename,
    ]);

    // ===== 差し替え =====

    const openReplace = useCallback((file: FileBrowserFile) => {
      replaceTargetRef.current = file;
      replaceInputRef.current?.click();
    }, []);

    const handleReplaceFileSelected = useCallback(
      async (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = replaceTargetRef.current;
        const nativeFile = e.target.files?.[0];
        // 同じファイルを再選択できるよう毎回リセットする
        e.target.value = "";
        if (!target || !nativeFile || isReplacing) return;

        setIsReplacing(true);
        const toastId = toast.loading(`「${target.name}」を差し替え中...`);
        try {
          await onReplaceFile?.(target, nativeFile);
          toast.success("ファイルを差し替えました", { id: toastId });
        } catch {
          toast.error("ファイルの差し替えに失敗しました", { id: toastId });
        } finally {
          replaceTargetRef.current = null;
          setIsReplacing(false);
        }
      },
      [isReplacing, onReplaceFile],
    );

    // ===== 削除 =====

    const handleDeleteConfirm = useCallback(async () => {
      if (!deleteFileTarget || isDeleting) return;
      setIsDeleting(true);
      try {
        await onDeleteFile?.(deleteFileTarget);
        toast.success("ファイルを削除しました");
        setDeleteFileTarget(null);
      } catch {
        toast.error("ファイルの削除に失敗しました");
      } finally {
        setIsDeleting(false);
      }
    }, [deleteFileTarget, isDeleting, onDeleteFile]);

    const handleDeleteFolderConfirm = useCallback(async () => {
      if (!deleteFolderTarget || isDeletingFolder) return;
      setIsDeletingFolder(true);
      try {
        await onDeleteFolder?.(deleteFolderTarget);
        toast.success("フォルダを削除しました");
        setDeleteFolderTarget(null);
      } catch {
        toast.error("フォルダの削除に失敗しました");
      } finally {
        setIsDeletingFolder(false);
      }
    }, [deleteFolderTarget, isDeletingFolder, onDeleteFolder]);

    // ===== フォルダ作成 =====

    const openCreateFolder = useCallback(
      (parentId: string | null = null, parentName: string | null = null) => {
        setCreateFolderState({ parentId, parentName });
        setCreateFolderName("");
      },
      [],
    );

    const handleCreateFolderConfirm = useCallback(async () => {
      if (!createFolderState || isCreatingFolder) return;
      const name = createFolderName.trim();
      if (!name) return;
      setIsCreatingFolder(true);
      try {
        const createdId = await onCreateFolder?.(
          name,
          createFolderState.parentId,
        );
        // 作成先を展開して新フォルダを見せる
        expandFolder(createFolderState.parentId);
        if (typeof createdId === "string") {
          setHighlightFolderId(createdId);
        }
        toast.success(`フォルダ「${name}」を作成しました`);
        setCreateFolderState(null);
        setCreateFolderName("");
      } catch {
        toast.error("フォルダの作成に失敗しました");
      } finally {
        setIsCreatingFolder(false);
      }
    }, [
      createFolderState,
      createFolderName,
      isCreatingFolder,
      onCreateFolder,
      expandFolder,
    ]);

    // ===== 外部ファイル / フォルダのD&Dアップロード =====

    const handleExternalDrop = useCallback(
      async (folderId: string | null, entries: FileWithPath[]) => {
        if (!onUploadFiles || entries.length === 0 || isUploading) return;
        setIsUploading(true);
        try {
          await onUploadFiles(entries, folderId);
          expandFolder(folderId);
        } finally {
          setIsUploading(false);
        }
      },
      [onUploadFiles, isUploading, expandFolder],
    );

    // 表示中の階層（ルート）へのアップロード可否。テーブル余白への外部D&D、
    // 末尾「+ ファイルを追加」行、空でもテーブルを出すかの判定に使う。
    // 余白へのD&Dはフォルダ行側で stopPropagation されるため、ここに届くのは
    // ルート直下へのドロップのみ
    const canUploadToRoot = canEditRoot && !!onUploadFiles;

    // 末尾「+ ファイルを追加」行 → 隠しファイル入力（表示中の階層へ追加）
    const addFileInputRef = useRef<HTMLInputElement>(null);
    const handleAddFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const entries: FileWithPath[] = Array.from(e.target.files ?? []).map(
          (file) => ({ file, relativePath: file.name }),
        );
        // 同じファイルを再選択できるよう毎回リセットする
        e.target.value = "";
        if (entries.length > 0) void handleExternalDrop(null, entries);
      },
      [handleExternalDrop],
    );

    const handleRootDragEnter = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      e.preventDefault();
      rootDragDepthRef.current += 1;
      setIsRootNativeOver(true);
    };

    const handleRootDragOver = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };

    const handleRootDragLeave = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      rootDragDepthRef.current = Math.max(0, rootDragDepthRef.current - 1);
      if (rootDragDepthRef.current === 0) setIsRootNativeOver(false);
    };

    // テーブル内のどこかへドロップされたら、ルートのホバーハイライトを解除する。
    // フォルダ行・追加行は bubble の drop を stopPropagation で止めるため、
    // どのドロップでも必ず先に届く capture 段階で enter カウンタごとリセットする
    const handleRootDropCapture = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      rootDragDepthRef.current = 0;
      setIsRootNativeOver(false);
    };

    /** ドロップされたファイル / フォルダを表示中の階層（ルート）直下へ上げる */
    const uploadDroppedEntriesToRoot = (e: React.DragEvent) => {
      void extractDropEntries(e).then((entries) => {
        if (entries.length > 0) handleExternalDrop(null, entries);
      });
    };

    const handleRootDrop = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      e.preventDefault();
      uploadDroppedEntriesToRoot(e);
    };

    // 末尾「+ ファイルを追加」行への外部D&D。テーブル余白と同じく表示中の
    // 階層（ルート）直下へのアップロードだが、行単位でハイライトを出して
    // 「開いている階層へ落とせる」ことを明示する。TableWrapper のルート
    // ドロップと二重処理にならないよう伝播を止める。
    const handleAddRowDragOver = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      setIsAddRowNativeOver(true);
    };

    const handleAddRowDrop = (e: React.DragEvent) => {
      if (!canUploadToRoot || !containsNativeFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setIsAddRowNativeOver(false);
      uploadDroppedEntriesToRoot(e);
    };

    // ===== テーブル内D&D（移動） =====

    const { resetDropDwell, handleDropDwellOver, isDropDwellSatisfied } =
      useDropDwellGate();

    const handleDragStart = useCallback(
      (event: DragStartEvent) => {
        resetDropDwell();
        const data = event.active.data.current as ActiveDrag | undefined;
        if (data) setActiveDrag(data);
      },
      [resetDropDwell],
    );

    // 移動結果は一覧の表示そのもので分かるため、成功トーストは出さない。
    const handleMoveFile = useCallback(
      async (file: FileBrowserFile, folderId: string | null) => {
        try {
          await onMoveFile?.(file, folderId);
          expandFolder(folderId);
        } catch {
          toast.error("ファイルの移動に失敗しました");
        }
      },
      [onMoveFile, expandFolder],
    );

    // フォルダ移動はファイルの単体移動と違い配下ごと動くため、確認を挟む。
    // 行全体がドラッグ元である以上、意図しないドロップの最後の歯止めになる。
    const requestMoveFolder = useCallback(
      (
        node: FileBrowserFolderNode,
        parentId: string | null,
        label: string,
      ) => {
        setMoveFolderTarget({ node, parentId, label });
      },
      [],
    );

    const handleMoveFolderConfirm = useCallback(async () => {
      if (!moveFolderTarget || isMovingFolder) return;
      const { node, parentId } = moveFolderTarget;
      setIsMovingFolder(true);
      try {
        await onMoveFolder?.(node, parentId);
        expandFolder(parentId);
        setMoveFolderTarget(null);
      } catch {
        toast.error("フォルダの移動に失敗しました");
      } finally {
        setIsMovingFolder(false);
      }
    }, [moveFolderTarget, isMovingFolder, onMoveFolder, expandFolder]);

    // ドラッグ中アイテムをルート（一番上の階層）へ移動できるか
    // （既にルートにあるものは対象外。ルートドロップバーの表示判定に使う）
    const canDropToRoot = useMemo(() => {
      if (!activeDrag || !canEditRoot) return false;
      if (activeDrag.kind === "file") {
        return (
          !!onMoveFile &&
          activeDrag.file.canEdit !== false &&
          activeDrag.file.folderId !== null
        );
      }
      return (
        !!onMoveFolder &&
        activeDrag.node.canEdit &&
        activeDrag.node.parentId !== null
      );
    }, [activeDrag, canEditRoot, onMoveFile, onMoveFolder]);

    const handleDragEnd = useCallback(
      async (event: DragEndEvent) => {
        const drag = activeDrag;
        const dwellSatisfied = isDropDwellSatisfied(event);
        setActiveDrag(null);
        if (!drag) return;

        // 移動先の上で一瞬しか止まっていないドロップは誤操作として扱う。
        // 移動先が青くなる前に離した = まだ移動しない、は見た目で伝わるので
        // ここでは黙って捨てる。
        if (!dwellSatisfied) return;

        // ルートドロップバーへのドロップ = 一番上の階層へ移動
        if (event.over?.id === ROOT_DROP_ID) {
          if (!canDropToRoot) return;
          if (drag.kind === "file") {
            await handleMoveFile(drag.file, null);
          } else {
            requestMoveFolder(drag.node, null, "ルート");
          }
          return;
        }

        const overData = event.over?.data.current as
          | { node: FileBrowserFolderNode }
          | undefined;
        if (!overData) return;

        const { node } = overData;
        if (!isValidDropTarget(drag, node)) return;

        if (drag.kind === "file") {
          await handleMoveFile(drag.file, node.id);
        } else {
          requestMoveFolder(drag.node, node.id, node.name);
        }
      },
      [
        activeDrag,
        canDropToRoot,
        handleMoveFile,
        isDropDwellSatisfied,
        requestMoveFolder,
      ],
    );

    // ===== 共有 =====

    const openShareFile = useCallback((file: FileBrowserFile) => {
      setShareTarget({ type: "file", id: file.id, name: file.name });
    }, []);

    const openShareFolder = useCallback((node: FileBrowserFolderNode) => {
      setShareTarget({ type: "folder", id: node.id, name: node.name });
    }, []);

    // ===== 命令的ハンドル =====
    // サイドバー等、テーブル外の導線から ID 指定でフォルダ操作を開く

    useImperativeHandle(
      ref,
      () => ({
        openCreateFolder: (parentId: string | null = null) =>
          openCreateFolder(
            parentId,
            parentId ? (folderNodesById.get(parentId)?.name ?? null) : null,
          ),
        openRenameFolder: (folderId: string) => {
          const node = folderNodesById.get(folderId);
          if (node) openRenameFolder(node);
        },
        openDeleteFolder: (folderId: string) => {
          const node = folderNodesById.get(folderId);
          if (node) setDeleteFolderTarget(node);
        },
        openShareFolder: (folderId: string) => {
          const node = folderNodesById.get(folderId);
          if (node) openShareFolder(node);
        },
        downloadFolder: (folderId: string) => {
          const node = folderNodesById.get(folderId);
          if (!node) return;
          if (node.totalCount === 0) {
            toast.error("フォルダにファイルがありません");
            return;
          }
          handleFolderDownload(node);
        },
        transcribeFolder: (folderId: string) => {
          if (!enableTranscription) return;
          const node = folderNodesById.get(folderId);
          if (!node) return;
          void handleFolderTranscribe(node);
        },
        moveFolderToRoot: (folderId: string) => {
          const node = folderNodesById.get(folderId);
          if (node && node.parentId !== null) {
            requestMoveFolder(node, null, "ルート");
          }
        },
      }),
      [
        folderNodesById,
        openCreateFolder,
        openRenameFolder,
        openShareFolder,
        handleFolderDownload,
        enableTranscription,
        handleFolderTranscribe,
        requestMoveFolder,
      ],
    );

    // ===== メニュー =====

    const buildFileMenuItems = useCallback(
      (file: FileBrowserFile): DropdownMenuItem[] => {
        const items: DropdownMenuItem[] = [];
        const canEdit = file.canEdit !== false;
        // オーディオは行クリックが試聴（選択）になるため、プレビューはメニューから開く
        if (file.fileType === "audio") {
          items.push({
            label: "プレビュー",
            icon: "visibility",
            onClick: () => setPreviewFile(file),
          });
        }
        items.push({
          label: "ダウンロード",
          icon: "download",
          onClick: () => handleDownload(file),
        });
        if (enableTranscription && isTranscribableFileType(file.fileType)) {
          items.push({
            label: "文字起こし",
            icon: "speech_to_text",
            onClick: () => setTranscriptionFile(file),
          });
        }
        if (shareAdapter) {
          items.push({
            label: "共有",
            icon: "share",
            onClick: () => openShareFile(file),
          });
        }
        if (onCopyLink) {
          items.push({
            label: "リンクをコピー",
            icon: "link",
            onClick: () =>
              onCopyLink({
                type: "file",
                id: file.id,
                name: file.name,
                folderId: file.folderId,
              }),
          });
        }
        if (canEdit && onRenameFile) {
          items.push({
            label: "リネーム",
            icon: "edit",
            onClick: () => openRenameFile(file),
          });
        }
        if (canEdit && onReplaceFile) {
          items.push({
            label: "差し替え",
            icon: "swap_horiz",
            onClick: () => openReplace(file),
            disabled: isReplacing,
          });
        }
        if (canEdit && onMoveFile && canEditRoot && file.folderId !== null) {
          items.push({
            label: "ルートへ移動",
            icon: "drive_file_move",
            onClick: () => handleMoveFile(file, null),
          });
        }
        if (canEdit && onDeleteFile) {
          items.push({
            label: "削除",
            icon: "delete",
            onClick: () => setDeleteFileTarget(file),
          });
        }
        return items;
      },
      [
        enableTranscription,
        shareAdapter,
        onCopyLink,
        onRenameFile,
        onReplaceFile,
        onMoveFile,
        onDeleteFile,
        canEditRoot,
        isReplacing,
        handleDownload,
        openShareFile,
        openRenameFile,
        openReplace,
        handleMoveFile,
      ],
    );

    // フォルダ行の3点リーダー（一括ダウンロードもここに集約）
    const buildFolderMenuItems = useCallback(
      (node: FileBrowserFolderNode): DropdownMenuItem[] => {
        const items: DropdownMenuItem[] = [
          {
            label: "一括ダウンロード",
            icon: "download",
            onClick: () => handleFolderDownload(node),
            disabled: isBulkDownloading || node.totalCount === 0,
          },
        ];
        if (enableTranscription) {
          items.push({
            label: "一括文字起こし",
            icon: "speech_to_text",
            onClick: () => handleFolderTranscribe(node),
            disabled: isResolvingTranscribeTargets || node.totalCount === 0,
          });
        }
        if (shareAdapter) {
          items.push({
            label: "共有",
            icon: "share",
            onClick: () => openShareFolder(node),
          });
        }
        if (onCopyLink) {
          items.push({
            label: "リンクをコピー",
            icon: "link",
            onClick: () =>
              onCopyLink({
                type: "folder",
                id: node.id,
                name: node.name,
                folderId: node.id,
              }),
          });
        }
        if (node.canEdit) {
          if (onCreateFolder) {
            items.push({
              label: "フォルダを作成",
              icon: "create_new_folder",
              onClick: () => openCreateFolder(node.id, node.name),
            });
          }
          if (onRenameFolder) {
            items.push({
              label: "リネーム",
              icon: "edit",
              onClick: () => openRenameFolder(node),
            });
          }
          if (onMoveFolder && canEditRoot && node.parentId !== null) {
            items.push({
              label: "ルートへ移動",
              icon: "drive_file_move",
              onClick: () => requestMoveFolder(node, null, "ルート"),
            });
          }
          if (onDeleteFolder) {
            items.push({
              label: "フォルダを削除",
              icon: "delete",
              onClick: () => setDeleteFolderTarget(node),
            });
          }
        }
        return items;
      },
      [
        enableTranscription,
        isResolvingTranscribeTargets,
        handleFolderTranscribe,
        shareAdapter,
        onCopyLink,
        onCreateFolder,
        onRenameFolder,
        onMoveFolder,
        onDeleteFolder,
        canEditRoot,
        isBulkDownloading,
        handleFolderDownload,
        openShareFolder,
        openCreateFolder,
        openRenameFolder,
        requestMoveFolder,
      ],
    );

    // ===== テーブル定義 =====

    const sortHeader = (label: string, field: FileBrowserSortField) => (
      <>
        <span>{label}</span>
        {sortState.field === field && (
          <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
            {sortState.order === "asc" ? "arrow_upward" : "arrow_downward"}
          </span>
        )}
      </>
    );

    const columns: TableColumn[] = [
      {
        key: "name",
        header: sortHeader("ファイル名", "name"),
        onHeaderClick: () => handleSort("name"),
        minWidth: 160,
      },
      {
        key: "updated",
        header: sortHeader("更新日", "updated"),
        onHeaderClick: () => handleSort("updated"),
        width: 140,
        minWidth: 100,
      },
      {
        key: "size",
        header: sortHeader("サイズ", "size"),
        onHeaderClick: () => handleSort("size"),
        width: 96,
        minWidth: 72,
      },
      { key: "actions", width: 48, minWidth: 48, resizable: false },
    ];

    const hasAnyItems = files.length > 0 || folders.length > 0;
    const renameTargetName = renameFileTarget?.name ?? renameFolderTarget?.name;

    return (
      <Container
        ref={containerRef}
        $bottomSpacing={
          inspectorFile
            ? AUDIO_INSPECTOR_BAR_HEIGHT + audioInspectorBottomOffset
            : 0
        }
      >
        {/* 閲覧専用（アップロード不可）で空のときだけ専用の空表示。
            アップロード可能なら空でもテーブル（空メッセージ + 追加行）を出す */}
        {!hasAnyItems && !isLoading && !canUploadToRoot ? (
          <EmptyState>
            <p className="bodytext u-text-light-gray">{emptyMessage}</p>
          </EmptyState>
        ) : (
          <DndContext
            sensors={dndSensors}
            // ポインタが実際に乗っている行だけを移動先にする。既定の
            // rectIntersection は掴んだ行の矩形が重なるだけで隣の行を移動先に
            // 選ぶため、ポインタが元の行にあるまま隣のフォルダへ移動してしまう。
            // 行の隙間で離した場合は移動先なし（何も起きない）になる。
            // 注意: ポインタ座標を使う判定なので、KeyboardSensor を足すときは
            // closestCenter との合成が必要になる。
            collisionDetection={pointerWithin}
            // 行の座標を常に測り直す。既定（WhileDragging）はドラッグ開始時の
            // 1 回だけで、ドラッグ中にテーブルがスクロールすると座標が古くなる。
            // rectIntersection は重なりが広いので当たっていたが、包含判定の
            // pointerWithin ではどの行にも当たらず移動できなくなる。
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={handleDragStart}
            onDragOver={handleDropDwellOver}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDrag(null)}
          >
            <DndTableArea>
              {/* ドラッグ中のみ現れるルート（一番上の階層）への移動先 */}
              {canDropToRoot && <RootMoveDropZone />}
              <TableWrapper
                $nativeOver={isRootNativeOver}
                onDragEnter={handleRootDragEnter}
                onDragOver={handleRootDragOver}
                onDragLeave={handleRootDragLeave}
                onDropCapture={handleRootDropCapture}
                onDrop={handleRootDrop}
              >
                <Table columns={columns} minWidth={480}>
                  {displayRows.length === 0 && !isLoading ? (
                    <tr>
                      <td colSpan={columns.length}>
                        <p className="bodytext-13 u-text-light-gray">
                          {/* アイテム自体はあるのに 0 件 = 検索で絞られた状態 */}
                          {hasAnyItems
                            ? "該当するファイルがありません"
                            : emptyMessage}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    displayRows.map((entry) => {
                      if (entry.kind === "folder") {
                        const { node } = entry;
                        return (
                          <FolderRow
                            key={`folder-${node.id}`}
                            node={node}
                            depth={entry.depth}
                            isExpanded={expandedFolders.has(node.id)}
                            draggable={!!onMoveFolder && node.canEdit}
                            activeDrag={activeDrag}
                            canNativeDrop={node.canEdit && !!onUploadFiles}
                            onToggle={toggleFolder}
                            onExternalDrop={handleExternalDrop}
                            buildMenuItems={buildFolderMenuItems}
                          />
                        );
                      }
                      if (entry.kind === "loading") {
                        // 展開したフォルダ直下のファイルを遅延取得している間の行
                        return (
                          <tr key={`loading-${entry.folderId}`}>
                            <td colSpan={columns.length}>
                              <p
                                className="bodytext-13 u-text-light-gray"
                                style={{
                                  margin: 0,
                                  paddingLeft: entry.depth * 20,
                                }}
                              >
                                読み込んでいます...
                              </p>
                            </td>
                          </tr>
                        );
                      }
                      const { file, depth } = entry;
                      return (
                        <FileRow
                          key={`file-${file.id}`}
                          file={file}
                          depth={depth}
                          draggable={!!onMoveFile && file.canEdit !== false}
                          auditionSelected={audition.selectedKey === file.id}
                          auditionPlaying={audition.playingKey === file.id}
                          onPreview={setPreviewFile}
                          onAuditionToggle={handleAuditionToggle}
                          onAuditionRowRef={handleAuditionRowRef}
                          onAuditionRowClick={handleAuditionRowClick}
                          onAuditionRowKeyDown={handleAuditionRowKeyDown}
                          buildMenuItems={buildFileMenuItems}
                          transcriptionStatus={transcriptionStatuses[file.id]}
                        />
                      );
                    })
                  )}
                  {/* データ読み込み中のローディング行 */}
                  {isLoading && (
                    <tr>
                      <td
                        colSpan={columns.length}
                        style={{ whiteSpace: "normal" }}
                      >
                        <InlineLoading visible size={24} />
                        <p
                          className="bodytext-13 u-text-light-gray"
                          style={{ textAlign: "center", margin: 0 }}
                        >
                          ファイルを読み込んでいます...
                        </p>
                      </td>
                    </tr>
                  )}
                  {/* 末尾「+」行: 新規ファイルの選択 / D&D 追加の入口。
                      行自体もドロップターゲットにして、フォルダごとの D&D を
                      表示中の階層（スコープ）直下へのアップロードとして受ける */}
                  {canUploadToRoot && (
                    <tr
                      onDragOver={handleAddRowDragOver}
                      onDragLeave={() => setIsAddRowNativeOver(false)}
                      onDrop={handleAddRowDrop}
                      style={
                        isAddRowNativeOver
                          ? {
                              background: "rgba(0, 154, 206, 0.10)",
                              boxShadow: "inset 0 0 0 2px var(--soremo-blue)",
                            }
                          : undefined
                      }
                    >
                      <td colSpan={columns.length}>
                        <AddFileRowButton
                          type="button"
                          onClick={() => addFileInputRef.current?.click()}
                          disabled={isUploading}
                          aria-label="ファイルを追加"
                          style={
                            isAddRowNativeOver
                              ? { color: "var(--soremo-blue)" }
                              : undefined
                          }
                        >
                          <span className="material-symbols-rounded">
                            {isAddRowNativeOver ? "upload" : "add"}
                          </span>
                          <span className="bodytext-13">
                            {isAddRowNativeOver
                              ? "ここにドロップでこの階層にアップロード"
                              : "クリックしてファイルを追加（フォルダごとのドラッグ＆ドロップも可）"}
                          </span>
                        </AddFileRowButton>
                      </td>
                    </tr>
                  )}
                </Table>
              </TableWrapper>
            </DndTableArea>

            {/* D&D 中にカーソルへ追従するゴースト */}
            <DragOverlay dropAnimation={null}>
              {activeDrag && (
                <DragGhost>
                  <span className="material-symbols-rounded">
                    {activeDrag.kind === "folder"
                      ? "folder"
                      : getFileIconFromCategoryOrExtension(
                          activeDrag.file.fileType,
                          activeDrag.file.name,
                        )}
                  </span>
                  <span>
                    {activeDrag.kind === "folder"
                      ? activeDrag.node.name
                      : activeDrag.file.name}
                  </span>
                </DragGhost>
              )}
            </DragOverlay>
          </DndContext>
        )}

        {/* 差し替え用の隠しファイル入力 */}
        <input
          ref={replaceInputRef}
          type="file"
          style={{ display: "none" }}
          onChange={handleReplaceFileSelected}
        />

        {/* 「+ ファイルを追加」行用の隠しファイル入力 */}
        {canUploadToRoot && (
          <input
            ref={addFileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleAddFileInputChange}
          />
        )}

        {/* 選択中オーディオの波形インスペクタ（範囲選択 + 範囲書き出し） */}
        {inspectorFile && (
          <AudioInspectorBar
            file={inspectorFile}
            transport={audition}
            onSelectPrev={audition.selectPrev}
            onSelectNext={audition.selectNext}
            bottomOffset={audioInspectorBottomOffset}
            onClose={() => setInspectorDismissed(true)}
            onExport={onFileDownloaded}
            bookmarks={audioBookmarks}
            onCreateBookmark={
              onCreateAudioBookmark
                ? (params) => onCreateAudioBookmark(inspectorFile.id, params)
                : undefined
            }
            onUpdateBookmark={onUpdateAudioBookmark}
            onDeleteBookmark={onDeleteAudioBookmark}
            // 対象ファイルを表示している間だけジャンプ指示を渡す
            focusRequest={
              audioFocusRequest && audioFocusRequest.fileId === inspectorFile.id
                ? audioFocusRequest
                : null
            }
            onFocusRequestApplied={onAudioFocusRequestApplied}
          />
        )}

        {/* プレビューモーダル */}
        {previewFile && (
          <MediaPreviewModal
            src={previewFile.optimizedUrl || previewFile.originalUrl}
            mediaType={toMediaType(previewFile.fileType)}
            fileName={previewFile.name}
            fileId={previewFile.id}
            waveformUrl={previewFile.waveformUrl || undefined}
            fileSize={previewFile.sizeBytes ?? undefined}
            createdAt={previewFile.updatedAt}
            downloadUrl={previewFile.originalUrl}
            onDownload={() => onFileDownloaded?.(previewFile.id)}
            onClose={() => setPreviewFile(null)}
          />
        )}

        {/* 単体の文字起こし（音声は試聴エンジンと連携してシークする） */}
        {transcriptionFile && (
          <MediaTranscriptionModal
            fileId={transcriptionFile.id}
            fileName={transcriptionFile.name}
            onClose={() => setTranscriptionFile(null)}
            onSeek={
              transcriptionFile.fileType === "audio"
                ? (seconds) => {
                    auditionSelectRef.current(transcriptionFile.id);
                    auditionSeekToRef.current(seconds);
                  }
                : undefined
            }
          />
        )}

        {/* フォルダ単位の一括文字起こし */}
        {bulkTranscription && (
          <BulkTranscriptionModal
            targets={bulkTranscription.targets}
            scopeLabel={bulkTranscription.scopeLabel}
            onClose={() => setBulkTranscription(null)}
          />
        )}

        {/* フォルダ一括ダウンロードの進捗モーダル */}
        {downloadState && (
          <BulkDownloadProgressModal
            state={downloadState}
            downloadName={downloadingFolderName}
            onClose={closeDownloadModal}
          />
        )}

        {/* 共有リンクモーダル */}
        {shareTarget && shareAdapter && (
          <FileBrowserShareModal
            target={shareTarget}
            adapter={shareAdapter}
            onClose={() => setShareTarget(null)}
          />
        )}

        {/* ファイル削除確認ダイアログ */}
        {deleteFileTarget && (
          <ConfirmModal
            message={`「${deleteFileTarget.name}」を削除しますか？`}
            confirmLabel="削除"
            loading={isDeleting}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteFileTarget(null)}
          />
        )}

        {/* フォルダ削除確認ダイアログ（中身ごと削除） */}
        {deleteFolderTarget && (
          <ConfirmModal
            message={
              deleteFolderTarget.totalCount > 0
                ? `フォルダ「${deleteFolderTarget.name}」を削除しますか？中のファイル ${deleteFolderTarget.totalCount}件もすべて削除されます`
                : `フォルダ「${deleteFolderTarget.name}」を削除しますか？`
            }
            confirmLabel="削除"
            loading={isDeletingFolder}
            onConfirm={handleDeleteFolderConfirm}
            onCancel={() => setDeleteFolderTarget(null)}
          />
        )}

        {/* フォルダ移動確認ダイアログ（配下ごと移動するため必ず確認する） */}
        {moveFolderTarget && (
          <ConfirmModal
            message={
              (moveFolderTarget.parentId === null
                ? `フォルダ「${moveFolderTarget.node.name}」をルートへ移動しますか？`
                : `フォルダ「${moveFolderTarget.node.name}」を「${moveFolderTarget.label}」の中へ移動しますか？`) +
              (moveFolderTarget.node.totalCount > 0
                ? `配下のファイル ${moveFolderTarget.node.totalCount}件も一緒に移動します`
                : "")
            }
            confirmLabel="移動"
            loading={isMovingFolder}
            onConfirm={handleMoveFolderConfirm}
            onCancel={() => setMoveFolderTarget(null)}
          />
        )}

        {/* フォルダ作成モーダル */}
        {createFolderState && (
          <Modal
            title={
              createFolderState.parentName
                ? `「${createFolderState.parentName}」内にフォルダを作成`
                : "フォルダを作成"
            }
            titleSize="medium"
            onClose={() => setCreateFolderState(null)}
            primaryAction={{
              label: "作成",
              onClick: handleCreateFolderConfirm,
              loading: isCreatingFolder,
              disabled: !createFolderName.trim(),
            }}
            secondaryAction={{
              label: "キャンセル",
              onClick: () => setCreateFolderState(null),
            }}
          >
            <RenameInput
              value={createFolderName}
              autoFocus
              placeholder="フォルダ名"
              onChange={(e) => setCreateFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  handleCreateFolderConfirm();
                }
              }}
            />
          </Modal>
        )}

        {/* リネームモーダル（ファイル / フォルダ共通） */}
        {(renameFileTarget || renameFolderTarget) && (
          <Modal
            title={renameFileTarget ? "ファイル名を変更" : "フォルダ名を変更"}
            titleSize="medium"
            onClose={closeRename}
            primaryAction={{
              label: "保存",
              onClick: handleRenameConfirm,
              loading: isRenaming,
              disabled:
                !renameDraft.trim() || renameDraft.trim() === renameTargetName,
            }}
            secondaryAction={{
              label: "キャンセル",
              onClick: closeRename,
            }}
          >
            <RenameInput
              value={renameDraft}
              autoFocus
              onChange={(e) => setRenameDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  handleRenameConfirm();
                }
              }}
            />
          </Modal>
        )}
      </Container>
    );
  },
);

FileBrowser.displayName = "FileBrowser";

export default FileBrowser;
